// SPDX-License-Identifier: Elastic-2.0
/**
 * useOfflineDetection — single source of truth for "is the network up?"
 * across the app (T-030).
 *
 * Strategy
 * ────────
 * - Listen to `window.online` + `window.offline` (browser hints).
 * - Track `lastOnlineAt` so the offline banner can render
 *   "Last update 2 m ago".
 * - Invalidate every TanStack query on `online` so cached data refreshes
 *   the moment the user reconnects.
 *
 * The hook is light enough to call from any component — it subscribes
 * to a single module-level store via `useSyncExternalStore`, so multiple
 * call sites share one listener pair.
 */
import { useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";

interface OfflineSnapshot {
  offline: boolean;
  lastOnlineAt: number | null;
}

let snapshot: OfflineSnapshot = {
  offline: typeof navigator !== "undefined" ? !navigator.onLine : false,
  lastOnlineAt: typeof navigator !== "undefined" && navigator.onLine ? Date.now() : null,
};

const listeners = new Set<() => void>();

function setSnapshot(next: Partial<OfflineSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  for (const listener of listeners) listener();
}

let installed = false;
function installListeners(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("online", () => setSnapshot({ offline: false, lastOnlineAt: Date.now() }));
  window.addEventListener("offline", () => setSnapshot({ offline: true }));
}

function subscribe(listener: () => void): () => void {
  installListeners();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): OfflineSnapshot {
  return snapshot;
}

/**
 * Read the current offline state + receive automatic re-renders when it
 * changes. Cheap; pass through to as many components as needed.
 */
export function useOfflineDetection(): OfflineSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Wire-up hook: call once near app root (DashboardLayout) so the query
 * cache invalidates when the browser comes back online. Separate from
 * the read-only hook so feature components can subscribe without
 * importing the query client.
 */
export function useOfflineQueryBridge(): void {
  const queryClient = useQueryClient();
  useSyncExternalStore(
    (listener) => {
      installListeners();
      const wrapped = (): void => {
        listener();
        if (!snapshot.offline) {
          // Just came back online — wake every query up.
          void queryClient.invalidateQueries();
        }
      };
      listeners.add(wrapped);
      return () => listeners.delete(wrapped);
    },
    getSnapshot,
    getSnapshot,
  );
}

/** Test-only: force a snapshot. Re-exported so tests don't need to fire DOM events. */
export const __testing__ = {
  setSnapshot,
  reset(): void {
    snapshot = { offline: false, lastOnlineAt: null };
    listeners.clear();
    installed = false;
  },
};
